import * as angular from 'angular';
import * as _ from 'lodash';

import { IAlarmService } from '../../../services';
import { AlarmList, Alarm, AlarmLevel, Difference, FlatGroup, Identifier, ComponentDefinition } from '../../../models';
import { ObjectDifference } from '../../../models/ObjectDifference';
import { GroupChangeBase, ICentralActions } from '../groupChangeBase';
import { guidEmpty } from '../../../common/';

interface Display {
    depth: number;
    diffs: Diff[];
    groupName: string;
    templateName: string;
}

interface Diff {
    guid: string;
    identifier: Identifier;
    alarms?: {
        text: string;
        guid: string;
        change: Difference;
    }[];
}

class GroupChangesAlarmsController extends GroupChangeBase implements angular.IComponentController {
    changes: ObjectDifference[];
    display: Display[];
    isLoading: boolean;
    loadingMessage: string;
    tuples: any;

    private _alarms: AlarmList;
    private _properties = {
        Low: 'low',
        High: 'high',
        States: 'states',
        Severity: 'level',
        Value: 'value',
        Action: 'comment',
        Weight: 'weight',
        POF: 'probabilityOfFailure',
        COF: 'consequenceOfFailure',
        Categories: 'categories',
        IsManual: 'isManual'
    };
    private _templateAlarms: AlarmList;

    static $inject = ['$q', '$translate', 'alarmService'];
    constructor(private _$q: angular.IQService, private _$translate: angular.translate.ITranslateService, private _alarmService: IAlarmService) {
        super();
    }

    $onInit() {
        this.refreshAlarms();
        if (this.master) {
            this.applyAllFromGroup = this.applyAllFromGroup.bind(this);
            this.applyAllFromTemplate = this.applyAllFromTemplate.bind(this);
            this.ignoreAll = this.ignoreAll.bind(this);
            this.share.diff.applyAllFromTemplate = this.applyAllFromTemplate;
            this.share.diff.applyAllFromGroup = this.applyAllFromGroup;
            this.share.diff.ignoreAll = this.ignoreAll;
        }
    }

    private refreshAlarms() {
        this.isLoading = true;
        this._$q
            .all([this._alarmService.getAll(), this._alarmService.getAllForTemplates()])
            .then((data) => {
                this._alarms = data[0];
                this._templateAlarms = data[1];

                this.refresh();
            })
            .finally(() => (this.isLoading = false));
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['groups'] || changesObj['templates'] || changesObj['changes']) {
            this.refreshAlarms();
        }
    }

    private applyAllFromTemplate() {
        this.share.central.filter((c) => c.rightAction && !this.isTreated(c.changeGuid)).forEach((c) => c.rightAction());
    }

    private applyAllFromGroup() {
        this.share.central.filter((c) => c.leftAction && !this.isTreated(c.changeGuid)).forEach((c) => c.leftAction());
    }

    private ignoreAll() {
        this.share.central.filter((c) => c.ignoreAction && !this.isTreated(c.changeGuid)).forEach((c) => c.ignoreAction());
    }

    private refresh(): void {
        this.display = [];
        this.share.central = [];
        this.share.usages = [];

        if (this.groups && this.templates && this.changes && this._alarms) {
            this.isLoading = true;
            this.display = this.groups.filter((g) => g.templateGuid !== guidEmpty).map((group) => this.mapGroup(group));
            this.isLoading = false;
        }
    }

    private mapGroup(group: FlatGroup) {
        this.share.central.push({});
        const groupIdentifiers = group.identifierCollection;
        const template = this.templates.find((t) => group.templateGuid === t.guid);

        const diffs: Diff[] = this.changes
            .filter((c) => groupIdentifiers.map((i) => i.guid).indexOf(c.objectGuid) >= 0)
            .map((c) => {
                this.share.central.push({});
                try {
                    const groupIdentifier = groupIdentifiers.find((i) => i.guid === c.objectGuid);
                    if (groupIdentifier) {
                        const templateIdentifier = template.identifierCollection.find((i) => i.guid === groupIdentifier.templateGuid);
                        const tmp = this.mapTemplateInformation(c, groupIdentifier, templateIdentifier);
                        return {
                            guid: group.guid,
                            identifier: groupIdentifier,
                            alarms: tmp
                        };
                    } else {
                        return null;
                    }
                } catch (e) {
                    console.error(e);
                    return {
                        guid: c.objectGuid,
                        identifier: {
                            name: c.objectGuid
                        } as any,
                        alarms: []
                    };
                }
            });
        return {
            groupName: group.name,
            templateName: template ? template.name : '',
            diffs: diffs.filter((d) => !!d),
            depth: (group as any).depth
        };
    }

    private mapTemplateInformation(c: ObjectDifference, groupIdentifier: Identifier, templateIdentifier: Identifier) {
        return c.differenceCollection.map((d) => {
            this.share.central.push(this.getCentralActions(d));
            const groupAlarms = groupIdentifier ? this._alarms[groupIdentifier.guid] || [] : [];
            const groupMatches = groupAlarms.filter((a) => a.guid === d.copyValue);
            const groupAlarm = groupMatches.length ? groupMatches[0] : null;
            const templateAlarms = templateIdentifier ? this._templateAlarms[templateIdentifier.guid] || [] : [];
            const alarmMatches = templateAlarms.filter((a) => a.guid === d.originalValue);
            const templateAlarm = alarmMatches.length ? alarmMatches[0] : null;
            const alarm = this.master ? templateAlarm : groupAlarm;
            return {
                text: d.property === 'TemplateGuid' ? this._$translate.instant('Template link is missing') : this.formatAlarm(alarm),
                guid: alarm ? alarm.guid : null,
                change: d
            };
        });
    }

    private getCentralActions(d: Difference): ICentralActions {
        return {
            leftAction: d.property === 'TemplateGuid' ? null : () => this.share.diff.applyFromGroup(d),
            leftDelete: !d.copyValue,
            leftSelected: () => this.share.selectedDiff.fromGroup[d.guid],
            ignoreAction: () => this.share.diff.ignore(d),
            ignoreSelected: () => this.share.selectedDiff.ignores[d.guid],
            rightAction: () => this.share.diff.applyFromTemplate(d),
            rightDelete: !d.originalValue,
            rightSelected: () => this.share.selectedDiff.fromTemplate[d.guid],
            changeGuid: d.guid
        };
    }

    private formatAlarm(alarm: Alarm): string {
        const missing = this._$translate.instant('Missing alarm');
        if (alarm) {
            const s: string[] = [];
            for (const key of Object.keys(this._properties)) {
                const prop = this._properties[key];
                const value: string = this.getValue(alarm, prop);
                s.push(`${this._$translate.instant(key)}: ${value}`);
            }
            return s.join(', ');
        } else {
            return missing;
        }
    }

    private getValue(alarm: Alarm, prop: any) {
        let value: string = alarm[prop];
        if (prop === 'level') {
            value = AlarmLevel[value];
        } else if (prop === 'states') {
            value = alarm[prop].length;
        } else if (prop === 'categories') {
            value = `${alarm[prop].length}`;
            if (alarm[prop].length) {
                value = `${value} (${alarm[prop].join(', ')})`;
            }
        } else if (prop === 'isManual') {
            value = !!value ? 'true' : 'false';
        }
        return value;
    }
}

export const wtGroupChangesAlarms: ComponentDefinition = {
    componentId: 'wtGroupChangesAlarms',
    controller: GroupChangesAlarmsController,
    template: require('./groupChangesAlarms.component.html'),
    controllerAs: 'vm',
    bindings: {
        changes: '<',
        groups: '<',
        templates: '<',
        master: '=?',
        share: '=',
        tuple: '<'
    }
};
